/**
 * Report fetchers and CSV downloader. The backend serves the same payload
 * for JSON and CSV at /reports/* — adding format=csv triggers the download.
 */

const API_BASE = import.meta.env.VITE_API_BASE_URL || "/api/v1";

export interface StopEventReportRow {
  service_date: string;
  route_short_name: string;
  route_long_name: string;
  headsign: string;
  driver_name: string;
  vehicle_label: string;
  trip_instance_id: string;
  stop_sequence: number;
  stop_name: string;
  stop_code: string;
  scheduled_arrival?: string;
  actual_arrival?: string;
  arrival_delay_seconds?: number;
  scheduled_departure?: string;
  actual_departure?: string;
  departure_delay_seconds?: number;
  arrival_source: string;
  departure_source: string;
  passengers_boarded?: number;
  passengers_alighted?: number;
}

export interface TripSummaryReportRow {
  service_date: string;
  route_short_name: string;
  route_long_name: string;
  headsign: string;
  driver_name: string;
  vehicle_label: string;
  trip_instance_id: string;
  status: string;
  scheduled_start?: string;
  scheduled_end?: string;
  actual_start?: string;
  actual_end?: string;
  total_stops: number;
  stops_served: number;
  stops_late: number;
  avg_delay_seconds?: number;
  max_delay_seconds?: number;
}

export interface ReportFilterParams {
  date_from: string;
  date_to: string;
  route_id?: string;
  vehicle_id?: string;
  driver_id?: string;
}

function buildQuery(params: ReportFilterParams): string {
  const qs = new URLSearchParams();
  qs.set("date_from", params.date_from);
  qs.set("date_to", params.date_to);
  if (params.route_id) qs.set("route_id", params.route_id);
  if (params.vehicle_id) qs.set("vehicle_id", params.vehicle_id);
  if (params.driver_id) qs.set("driver_id", params.driver_id);
  return qs.toString();
}

const AUTH_STORAGE_KEY = "occ.session.v1";

function currentAccessToken(): string | null {
  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { accessToken?: string };
    return parsed.accessToken ?? null;
  } catch {
    return null;
  }
}

async function authedFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers);
  const token = currentAccessToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);
  const res = await fetch(`${API_BASE}${path}`, { ...init, headers });
  if (!res.ok) {
    let bodyText = "";
    try { bodyText = await res.text(); } catch { /* ignore */ }
    throw new Error(`HTTP ${res.status}: ${bodyText || res.statusText}`);
  }
  return res;
}

export async function fetchStopEventsReport(params: ReportFilterParams): Promise<StopEventReportRow[]> {
  const res = await authedFetch(`/reports/stop-events?${buildQuery(params)}`);
  return res.json();
}

export async function fetchTripSummaryReport(params: ReportFilterParams): Promise<TripSummaryReportRow[]> {
  const res = await authedFetch(`/reports/trip-summary?${buildQuery(params)}`);
  return res.json();
}

/**
 * downloadReportCsv triggers a browser download for the CSV variant of a
 * report. We fetch with auth then synthesize an <a download> click so the
 * filename header from the backend is respected.
 */
export async function downloadReportCsv(
  kind: "stop-events" | "trip-summary",
  params: ReportFilterParams,
): Promise<void> {
  const qs = buildQuery(params) + "&format=csv";
  const res = await authedFetch(`/reports/${kind}?${qs}`);
  const blob = await res.blob();
  const url = window.URL.createObjectURL(blob);
  const disposition = res.headers.get("Content-Disposition") || "";
  const match = /filename="([^"]+)"/.exec(disposition);
  const filename = match ? match[1] : `${kind}-${params.date_from}-${params.date_to}.csv`;
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}

/**
 * downloadStioXlsx pulls the Stop In/Out workbook for one route, direction and
 * service date. Unlike the CSV reports this is one file per route+direction+day
 * by design — that is the shape operations already files them in.
 */
export async function downloadStioXlsx(params: {
  route_id: string;
  direction_id: 0 | 1;
  service_date: string;
}): Promise<void> {
  const qs = new URLSearchParams({
    route_id: params.route_id,
    direction_id: String(params.direction_id),
    service_date: params.service_date,
  }).toString();
  const res = await authedFetch(`/reports/stio?${qs}`);
  const blob = await res.blob();
  const url = window.URL.createObjectURL(blob);
  const disposition = res.headers.get("Content-Disposition") || "";
  const match = /filename="([^"]+)"/.exec(disposition);
  const filename = match ? match[1] : `stio-${params.service_date}.xlsx`;
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}

/** detectTrips re-runs movement-based trip detection for a service date. */
export async function detectTrips(service_date: string): Promise<{
  trips_detected: number;
  reconciled: number;
  flagged: number;
  vehicles_scanned: number;
}> {
  const res = await authedFetch(`/reports/detect-trips?service_date=${service_date}`, {
    method: "POST",
  });
  return res.json();
}
